import { useState } from 'react';
import { CHRONICLE_LOOK } from '@continuum/show-core';
import { drawChronicleCard } from '../ui/chronicleCard.js';
import type { Alignment, Profession } from '@continuum/show-core';
import { Screen } from '../ui/Shell.js';
import { GhostButton, mono, sans } from '../ui/atoms.js';
import type { ScreenCopy } from '../ui/screenText.js';
import type { Lang } from '../ui/text.js';

/**
 * The end — your results. The call sign, the job, three real things from the evening, and the
 * role, shown here to the guest for the first time.
 */
export function Record({
  s,
  lang,
  callsign,
  profession,
  alignment,
  outcome,
  damage = 0,
  uprising = false,
  contributions,
  onNext,
}: {
  s: ScreenCopy;
  lang: Lang;
  callsign: string;
  profession: Profession | null;
  alignment: Alignment | null;
  /** Set when the ship docked. There is no winner: only the damage it came home with (2026-09-29). */
  outcome: 'home' | 'crew' | 'agents' | null;
  /** The damage the ship came home with, all evening. */
  damage?: number;
  /** Whether the saboteurs took The Boom and set the machines loose. */
  uprising?: boolean;
  contributions: string[];
  onNext: () => void;
}) {
  const alignColor = alignment === 'PRESERVER' ? '#4fd8c2' : '#ff5f86';
  const [saving, setSaving] = useState<'idle' | 'busy' | 'done' | 'failed'>('idle');

  /** The card as a picture: the share sheet where the phone has one (Save Image), a download otherwise. */
  const saveCard = async () => {
    setSaving('busy');
    try {
      const blob = await drawChronicleCard({ lang, callsign, profession, alignment, outcome, uprising, contributions });
      const file = new File([blob], `ss-aurora-${callsign.toLowerCase()}.png`, { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: s.cardTitle });
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5_000);
      }
      setSaving('done');
    } catch (err) {
      // Closing the share sheet is a choice, not a failure.
      setSaving(err instanceof DOMException && err.name === 'AbortError' ? 'idle' : 'failed');
    }
  };

  return (
    <Screen
      style={{ background: 'linear-gradient(180deg,#0e0c07,#12100a)', color: '#f3ead6', padding: 24 }}
      footer={
        <GhostButton onClick={onNext} color={CHRONICLE_LOOK.acc} border="rgba(217,185,106,.5)">
          {s.next} →
        </GhostButton>
      }
    >
      <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 2, color: CHRONICLE_LOOK.acc }}>{s.resultsKicker}</div>
      <h1 style={{ margin: '12px 0 16px', fontSize: 26, fontWeight: 700, letterSpacing: 2 }}>{s.resultsTitle}</h1>

      <div
        style={{
          border: `1px solid ${CHRONICLE_LOOK.acc}55`,
          borderRadius: 14,
          padding: 18,
          background: 'rgba(217,185,106,.05)',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: '4px 12px' }}>
          <div style={{ minWidth: 0, fontSize: 28, fontWeight: 700, letterSpacing: 3, fontFamily: sans }}>{callsign}</div>
          <div style={{ fontFamily: mono, fontSize: 12, letterSpacing: 1.2, color: '#b9a678' }}>{profession}</div>
        </div>

        <div style={{ marginTop: 14, fontFamily: mono, fontSize: 13, lineHeight: 1.7, color: '#dcd0b4' }}>
          {contributions.map((line) => (
            <div key={line}>· {line}</div>
          ))}
        </div>

        <div
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: '1px dashed rgba(217,185,106,.35)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ fontFamily: mono, fontSize: 11, letterSpacing: 2, color: '#b09e74' }}>{s.role}</span>
          <span style={{ fontFamily: sans, fontSize: 16, fontWeight: 700, letterSpacing: 2.5, color: alignColor }}>
            {alignment === 'PARADOX AGENT' ? s.roleSaboteur : alignment === 'PRESERVER' ? s.roleCrew : '—'}
          </span>
        </div>
      </div>

      {uprising && (
        <div style={{ marginTop: 16, fontFamily: mono, fontSize: 12, lineHeight: 1.55, letterSpacing: 0.5, color: '#ff9fb3' }}>{s.uprisingLine}</div>
      )}

      {/* No winner (Marek, 2026-09-29): the ship came home, and this is the damage it took. */}
      {outcome && (
        <div
          style={{
            marginTop: 16,
            borderRadius: 14,
            padding: '16px 18px',
            border: '1px solid #d9b96a',
            background: 'rgba(217,185,106,.08)',
          }}
        >
          <div style={{ fontFamily: sans, fontSize: 20, fontWeight: 700, letterSpacing: 2, color: '#d9b96a' }}>{s.cameHome(damage)}</div>
        </div>
      )}

      <div style={{ marginTop: 16, fontFamily: mono, fontSize: 13, lineHeight: 1.6, color: '#b09e74' }}>{s.thanksSaboteurs}</div>

      <button
        onClick={() => void saveCard()}
        disabled={saving === 'busy'}
        style={{
          marginTop: 16,
          width: '100%',
          textAlign: 'left',
          border: '1px dashed rgba(217,185,106,.55)',
          borderRadius: 12,
          padding: '14px 16px 15px',
          height: 'auto',
          lineHeight: 1.3,
          background: 'rgba(217,185,106,.06)',
          color: '#f3ead6',
          cursor: 'pointer',
        }}
      >
        <div style={{ fontFamily: sans, fontSize: 16, fontWeight: 700, letterSpacing: 2 }}>
          {saving === 'busy' ? s.savingCard : saving === 'done' ? s.savedCard : s.saveCard}
          <span
            style={{
              display: 'block',
              marginTop: 5,
              lineHeight: 1.5,
              fontFamily: mono,
              fontSize: 12,
              fontWeight: 400,
              letterSpacing: 0.5,
              color: saving === 'failed' ? '#ff9f8f' : '#b09e74',
            }}
          >
            {saving === 'failed' ? s.saveCardFailed : s.saveCardSub}
          </span>
        </div>
      </button>
    </Screen>
  );
}

/** The end: the survey's answer, in the story's own words (NARRATION.md), against the scale of the evening. */
export function Clock({ s, onHome }: { s: ScreenCopy; onHome: () => void }) {
  const scales: [string, string, number][] = [
    [s.scaleEvening, s.scaleEveningValue, 4],
    [s.scaleVoyage, s.scaleVoyageValue, 34],
    [s.scaleUniverse, s.scaleUniverseValue, 100],
  ];

  return (
    <Screen
      style={{ background: 'linear-gradient(180deg,#0e0c07,#12100a)', color: '#f3ead6', padding: 24 }}
      footer={
        <GhostButton onClick={onHome} color={CHRONICLE_LOOK.acc} border="rgba(217,185,106,.5)">
          {s.next} →
        </GhostButton>
      }
    >
      <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 2, color: CHRONICLE_LOOK.acc }}>{s.answerKicker}</div>
      <h1 style={{ margin: '14px 0 10px', fontSize: 26, fontWeight: 700, letterSpacing: 2, lineHeight: 1.2, textWrap: 'balance' }}>
        {s.answerTitle}
      </h1>
      <div style={{ fontFamily: mono, fontSize: 14, color: '#cdbd92', lineHeight: 1.6 }}>{s.answerBody}</div>

      <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {scales.map(([label, value, width]) => (
          <div key={label}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                gap: '2px 12px',
                fontFamily: mono,
                fontSize: 12,
                letterSpacing: 1.2,
                color: '#cdbd92',
              }}
            >
              <span>{label}</span>
              <span>{value}</span>
            </div>
            <div style={{ marginTop: 5, height: 5, borderRadius: 3, background: 'rgba(255,255,255,.08)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${width}%`, background: CHRONICLE_LOOK.acc, opacity: 0.75 }} />
            </div>
          </div>
        ))}
      </div>
    </Screen>
  );
}

/** The very end: the ship is home, and so is everyone on it. */
export function Farewell({
  s,
  callsign,
  onBack,
  wrapped,
}: {
  s: ScreenCopy;
  callsign: string;
  onBack: () => void;
  /** The evening as story cards, opened from here: the last thing the phone offers. */
  wrapped?: { label: string; sub: string; onOpen: () => void };
}) {
  return (
    <Screen
      style={{
        background:
          'radial-gradient(ellipse at 50% 28%, rgba(217,185,106,.18), transparent 62%), linear-gradient(180deg,#0e0c07,#12100a)',
        color: '#f3ead6',
        padding: 28,
        textAlign: 'center',
      }}
      footer={
        <>
          {wrapped && (
            <button
              onClick={wrapped.onOpen}
              style={{
                minHeight: 60,
                borderRadius: 12,
                border: 'none',
                background: CHRONICLE_LOOK.acc,
                color: '#17130a',
                cursor: 'pointer',
              }}
            >
              <span style={{ display: 'block', fontFamily: sans, fontSize: 17, fontWeight: 700, letterSpacing: 2.5 }}>{wrapped.label}</span>
              <span style={{ display: 'block', marginTop: 2, fontFamily: mono, fontSize: 12, letterSpacing: 0.3 }}>{wrapped.sub}</span>
            </button>
          )}
          <GhostButton onClick={onBack} color={CHRONICLE_LOOK.acc} border="rgba(217,185,106,.5)">
            {s.backToResults}
          </GhostButton>
        </>
      }
    >
      <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 2.5, color: CHRONICLE_LOOK.acc }}>{s.homeKicker}</div>
      <div style={{ flex: 1, minHeight: 20 }} />
      <div
        style={{
          margin: '0 auto',
          width: 104,
          height: 104,
          // A ring, never an oval: on a short phone the spacers give way and the screen scrolls.
          flex: 'none',
          borderRadius: '50%',
          border: `1px solid ${CHRONICLE_LOOK.acc}66`,
          display: 'grid',
          placeItems: 'center',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: -8,
            borderRadius: '50%',
            border: `1px dashed ${CHRONICLE_LOOK.acc}33`,
            animation: 'ccSwirl 30s linear infinite',
          }}
        />
        <div style={{ fontFamily: sans, fontSize: 42, fontWeight: 700, color: CHRONICLE_LOOK.acc }}>✦</div>
      </div>
      <h1
        style={{
          margin: '26px 0 12px',
          fontFamily: sans,
          fontSize: 32,
          fontWeight: 700,
          letterSpacing: 2,
          lineHeight: 1.1,
          textWrap: 'balance',
        }}
      >
        {s.thanksTitle}
      </h1>
      <p style={{ margin: '0 auto', maxWidth: 320, fontFamily: sans, fontSize: 19, lineHeight: 1.4, color: '#dcd0b4' }}>{s.thanksBody}</p>
      <div style={{ marginTop: 24, fontFamily: sans, fontSize: 22, fontWeight: 700, letterSpacing: 3, color: CHRONICLE_LOOK.acc, overflowWrap: 'anywhere' }}>
        {s.safeTravels(callsign)}
      </div>
      <div style={{ flex: 1, minHeight: 20 }} />
    </Screen>
  );
}
